import React, { useRef, useEffect } from 'react';
import logger from '../../utils/logger';
import { graphics, ChessColor } from './ChessLib';
import { ChessGame, Player } from './types';
import './Moves.css';

interface PlayerMove {
    piece?: string;
    color: string;
    from: string;
    to: string;
    capturedPiece?: string | null;
}

interface MovesProps {
    game: ChessGame | null;
}

function Moves({ game }: MovesProps) {
    const tableWrapperRef = useRef<HTMLDivElement>(null);

    // Get players and moves before early returns
    const whitePlayer = game?.players.find(p => p.color === ChessColor.WHITE);
    const blackPlayer = game?.players.find(p => p.color === ChessColor.BLACK);
    const whiteMoves = (whitePlayer?.moves as PlayerMove[] | undefined) || [];
    const blackMoves = (blackPlayer?.moves as PlayerMove[] | undefined) || [];

    // Debug logging
    if (whiteMoves.length > 0) {
        logger.debug('White player moves', { count: whiteMoves.length, firstMove: whiteMoves[0] });
    }
    if (blackMoves.length > 0) {
        logger.debug('Black player moves', { count: blackMoves.length, firstMove: blackMoves[0] });
    }

    // Combine moves with move number (white moves are odd numbered, black are even)
    const allMoves: { moveNum: number; player: 'plain' | 'charcoal'; move: PlayerMove }[] = [];

    whiteMoves.forEach((move, index) => {
        if (move.capturedPiece) {
            logger.debug('White move with capture', { moveNum: index * 2 + 1, capturedPiece: move.capturedPiece });
        }
        allMoves.push({
            moveNum: index * 2 + 1,
            player: 'plain',
            move: { ...move, color: ChessColor.WHITE }
        });
    });

    blackMoves.forEach((move, index) => {
        if (move.capturedPiece) {
            logger.debug('Black move with capture', { moveNum: index * 2 + 2, capturedPiece: move.capturedPiece });
        }
        allMoves.push({
            moveNum: index * 2 + 2,
            player: 'charcoal',
            move: { ...move, color: ChessColor.BLACK }
        });
    });

    // Sort by move number ascending (oldest first)
    allMoves.sort((a, b) => a.moveNum - b.moveNum);

    // Auto-scroll to bottom when moves change (MUST be before early returns)
    useEffect(() => {
        if (tableWrapperRef.current) {
            tableWrapperRef.current.scrollTop = tableWrapperRef.current.scrollHeight;
        }
    }, [allMoves.length]);

    // Don't return null - always show the table structure

    return (
        <div className="moves-container">
            <h2 style={{ textAlign: 'center', marginBottom: '15px', fontSize: '28px' }}>Move History</h2>
            <div className="moves-table-wrapper" ref={tableWrapperRef}>
                <table className="moves-table">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Player</th>
                            <th>Piece</th>
                            <th>From</th>
                            <th>To</th>
                            <th>Captured</th>
                        </tr>
                    </thead>
                    <tbody>
                        {allMoves.length === 0 ? (
                            <tr>
                                <td colSpan={6} style={{ textAlign: 'center' }}>No moves yet</td>
                            </tr>
                        ) : (
                            allMoves.map((item, index) => (
                                <tr key={`move-${index}`} className={`move-row-${item.player}`}>
                                    <td className="move-number-cell">{item.moveNum}</td>
                                    <td className="player-cell">{item.player}</td>
                                    <td className="piece-cell">
                                        {item.move.piece && (
                                            <img
                                                src={graphics[`${item.move.piece.toLowerCase()}-${item.player === 'plain' ? "white" : "black"}`]}
                                                alt={`piece ${item.move.piece}`}
                                                className="piece-img-moves"
                                            />
                                        )}
                                    </td>
                                    <td className="position-cell">{item.move.from}</td>
                                    <td className="position-cell">{item.move.to}</td>
                                    <td className="piece-cell">
                                        {item.move.capturedPiece && (
                                            <img
                                                src={graphics[`${item.move.capturedPiece.toLowerCase()}-${item.player === 'plain' ? "black" : "white"}`]}
                                                alt={`captured ${item.move.capturedPiece}`}
                                                className="piece-img-moves"
                                            />
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default Moves;
